import { colors } from "@styles/colors";
import { useEffect, useState } from "react";
import { mockMatchByAddress } from "../mockData";

interface MatchingStepProps {
  /** 用於 mock 配對規則（末碼為 0 → 候補）。 */
  phone: string;
  onRecommend: () => void;
  onWaitlist: () => void;
}

/**
 * 地址配對中的過場動畫（雷達掃描）。動畫需精細控制，故以原生 canvas 級別的 inline
 * style 呈現。實際配對呼叫 mockData 的模擬函式（待接後端 API）。
 * 註：手機配對已改於 OTP 驗證時同步完成，不再走此過場。
 */
const MatchingStep = ({ phone, onRecommend, onWaitlist }: MatchingStepProps) => {
  const [stage, setStage] = useState(0);

  const stages = ["搜尋附近合作瓦斯行…", "評估服務範圍與配送時間…", "為您準備最佳推薦…"];
  const title = "為您配對中";

  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 900);
    const t2 = setTimeout(() => setStage(2), 1700);
    let cancelled = false;

    mockMatchByAddress(phone).then(({ type }) => {
      if (cancelled) return;
      if (type === "recommend") onRecommend();
      else onWaitlist();
    });

    return () => {
      cancelled = true;
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [phone]);

  return (
    <div
      style={{
        minHeight: "100dvh",
        width: "100vw",
        background: colors.bg,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 32px",
        fontFamily: "var(--font-sans)",
      }}>
      <style>{`
        @keyframes nu-pulse{0%,100%{transform:scale(1);opacity:0.5}50%{transform:scale(1.15);opacity:0.8}}
        @keyframes nu-fadeInOut{0%,100%{opacity:0.3}50%{opacity:1}}
        @keyframes nu-radarSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
      `}</style>

      <div style={{ position: "relative", width: 120, height: 120, marginBottom: 32 }}>
        <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "rgba(33,150,243,0.12)", animation: "nu-pulse 2s ease-in-out infinite" }} />
        <div style={{ position: "absolute", inset: 12, borderRadius: "50%", background: "rgba(33,150,243,0.18)", animation: "nu-pulse 2s ease-in-out infinite 0.3s" }} />
        <div
          style={{
            position: "absolute",
            inset: 28,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #2196f3, #1976D2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 6px 20px rgba(33,150,243,0.4)",
            overflow: "hidden",
          }}>
          <svg width="44" height="44" viewBox="0 0 44 44" fill="none" style={{ position: "absolute" }}>
            <circle cx="22" cy="22" r="18" stroke="rgba(255,255,255,0.25)" strokeWidth="1" fill="none" />
            <circle cx="22" cy="22" r="12" stroke="rgba(255,255,255,0.35)" strokeWidth="1" fill="none" />
            <circle cx="22" cy="22" r="6" stroke="rgba(255,255,255,0.5)" strokeWidth="1" fill="none" />
            <circle cx="22" cy="22" r="2.5" fill="#fff" />
          </svg>
          <div style={{ position: "absolute", inset: 0, animation: "nu-radarSpin 2s linear infinite", transformOrigin: "center" }}>
            <svg width="100%" height="100%" viewBox="0 0 64 64" fill="none">
              <defs>
                <linearGradient id="nu-radar-sweep" x1="50%" y1="50%" x2="100%" y2="50%">
                  <stop offset="0%" stopColor="rgba(255,255,255,0)" />
                  <stop offset="100%" stopColor="rgba(255,255,255,0.55)" />
                </linearGradient>
              </defs>
              <path d="M 32 32 L 60 32 A 28 28 0 0 0 53.8 14.6 Z" fill="url(#nu-radar-sweep)" />
            </svg>
          </div>
        </div>
      </div>

      <div style={{ fontSize: 22, fontWeight: 800, color: colors.grayDark, marginBottom: 12, textAlign: "center" }}>{title}</div>

      <div style={{ minHeight: 50, display: "flex", flexDirection: "column", gap: 6, alignItems: "center" }}>
        {stages.map((s, i) => (
          <div
            key={i}
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: i === stage ? colors.primary : i < stage ? colors.statusCompleted : colors.hint,
              opacity: i <= stage ? 1 : 0.3,
              transition: "all 0.4s",
              animation: i === stage ? "nu-fadeInOut 1.2s ease-in-out infinite" : "none",
            }}>
            {i < stage ? "✓ " : ""}
            {s}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MatchingStep;
